/**
 * @file part-patches.ts
 * @description Сборка тел правки частей BRAND, SEO, BLOG из значения формы
 * (phase19.16 Р1; контракт §1 и §13 — частичное тело: отсутствующее поле не
 * трогается, `null` очищает, ссылки — id, пустая строка = `null` по SB21).
 *
 * Форма хранит значение в виде ответа сервера (картинка и страница —
 * объектом ради миниатюры и названия, коды подтверждения — объектом
 * `verification`), а правка принимает плоские поля. Соответствие «поле
 * формы → поля тела» задано реестрами (OCP: новое поле части — строка
 * реестра, а не ветка).
 */

import type { SiteBlogPatch, SiteBrandPatch } from "../api-site-view";
import type { SiteBlogDraft, SiteBrandDraft } from "./types";

/** Пустая или пробельная строка — «не задано» (SB21). */
export function emptyToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return value.trim() === "" ? null : value;
}

/** Поле-ссылка формы (`{ id, … }` или `null`) → поле id в теле. */
type RefKeyMap<V> = Partial<Record<keyof V, string>>;
/** Поле-объект формы → несколько плоских полей тела (`verification.yandex` → `yandexVerification`). */
type SplitKeyMap<V> = Partial<Record<keyof V, Readonly<Record<string, string>>>>;

interface PatchMap<V> {
  refs: RefKeyMap<V>;
  split?: SplitKeyMap<V>;
}

const BRAND_MAP: PatchMap<SiteBrandDraft> = {
  refs: {
    logoLight: "logoLightMediaId",
    logoDark: "logoDarkMediaId",
    favicon: "faviconMediaId",
    footerDecor: "footerDecorMediaId",
    deliveryTermsPage: "deliveryTermsPageId",
  },
};
const BLOG_MAP: PatchMap<SiteBlogDraft> = { refs: { cover: "coverMediaId" } };

/** Поля, которые отправляются как есть (не строки или не очищаются). */
const VERBATIM = new Set(["siteName", "contacts", "allowIndexing"]);
/** Только для чтения: в тело не уходят. */
const READ_ONLY = new Set(["shopContactEmail"]);

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

/** Строковое значение тела: пустое → `null`. */
function textValue(raw: unknown): unknown {
  return typeof raw === "string" ? emptyToNull(raw) : (raw ?? null);
}

function buildPatch<V extends object>(value: V, keys: ReadonlySet<string>, map: PatchMap<V>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const key of keys) {
    if (READ_ONLY.has(key) || !(key in value)) continue;
    const raw = (value as Record<string, unknown>)[key];
    // Название сайта очистить нельзя (контракт §1): пустое не отправляем.
    if (key === "siteName" && (typeof raw !== "string" || raw.trim() === "")) continue;
    const refKey = map.refs[key as keyof V];
    const split = map.split?.[key as keyof V];
    if (refKey) {
      body[refKey] = isRecord(raw) && typeof raw.id === "string" ? raw.id : null;
    } else if (split) {
      const source = isRecord(raw) ? raw : {};
      for (const [from, to] of Object.entries(split)) body[to] = textValue(source[from]);
    } else if (VERBATIM.has(key)) {
      body[key] = raw ?? null;
    } else {
      body[key] = textValue(raw);
    }
  }
  return body;
}

export function brandPatch(value: SiteBrandDraft, keys: ReadonlySet<string>): SiteBrandPatch {
  return buildPatch(value, keys, BRAND_MAP) as SiteBrandPatch;
}

export function blogPatch(value: SiteBlogDraft, keys: ReadonlySet<string>): SiteBlogPatch {
  return buildPatch(value, keys, BLOG_MAP) as SiteBlogPatch;
}
